import { IconFolderOpenAngledOutline } from '@icons/IconFolderOpenAngledOutline'
import { IconSearchOutline } from '@icons/IconSearchOutline'
import { IconRightChevronOutline } from '@icons/IconRightChevronOutline'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { useCallback, useMemo, useState } from 'react'
import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import { SearchInput } from '../shared/SearchInput'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import { getComponentHeights } from '../utils'
import { IndexSelectorV2 } from './components/IndexSelectorV2'
import { IndexCreateSheet } from './components/IndexSelectorV2/IndexCreateSheet'
import { IndexContentCreateSheet } from './components/IndexSelectorV2/IndexContentCreateSheet'
import { IndexPanelActions } from './components/IndexSelectorV2/IndexPanelActions'
import { useIndexReorder } from './components/IndexSelectorV2/useIndexReorder'
import {
  StatementEditorV2ContentDataTypeWithPosition,
  StatementEditorV2DataSchema,
  StatementEditorV2UISchema,
} from './types'
import { isStatementEditorV2UISchema } from './types'
import { StatementEditorV2EmptyResponse } from './StatementEditorV2EmptyResponse'
import { ContentBlockActions, ItemColumnsLayoutV2, ItemHeaderLayoutV2 } from './components'
import { useTileFilters } from '@/catalog-apps/hooks/useTileFilters'
import { useSubmitForm } from '../FormComponent/useSubmitForm'
import clsx from 'clsx'

export function StatementEditorV2Content({
  uiSchema,
  dataSchema,
  tileFilters,
}: {
  uiSchema: StatementEditorV2UISchema
  dataSchema: StatementEditorV2DataSchema
  tileFilters?: React.ReactNode
}) {
  const {
    title,
    description,
    emphasizeHeader,
    columns,
    fields,
    actions = [],
    headerActions = [],
    reOrderHandlerId,
    addContentFormContentId,
  } = uiSchema
  const { indexData, contentData } = dataSchema
  const hasHeaderActions = headerActions.length > 0
  const hasHeader = (!!title && !!tileFilters) || hasHeaderActions
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)
  const { tileFilterSelections } = useTileFilters()
  const { mutate: submitReorder, isPending: isSavingReorder } = useSubmitForm(
    reOrderHandlerId ?? '',
    tileFilterSelections
  )

  const [selectedContentIds, setSelectedContentIds] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const typedIndexData = indexData
  const existingContentIds = useMemo(() => contentData.map((content) => content.id), [contentData])
  const {
    isReorderMode,
    isCreateSheetOpen,
    setIsCreateSheetOpen,
    isAddContentSheetOpen,
    setIsAddContentSheetOpen,
    enterReorderMode,
    cancelReorder,
    exitReorderMode,
    getSerializedIndexData,
    getRemovedContentRefIds,
    getNewContentItems,
    pendingContentItems,
    handleAddNode,
    handleAddContent,
    reorderSelector,
  } = useIndexReorder(typedIndexData, existingContentIds, !!addContentFormContentId)

  const handleSaveReorder = useCallback(() => {
    if (!reOrderHandlerId) {
      return
    }

    const removedContentIds = getRemovedContentRefIds()
    const newContentItems = getNewContentItems()

    submitReorder(
      {
        indexData: JSON.stringify(getSerializedIndexData()),
        deletedContentIds: JSON.stringify(removedContentIds),
        newContentItems: JSON.stringify(newContentItems),
      },
      {
        onSuccess: () => {
          if (removedContentIds.length > 0) {
            setSelectedContentIds((prev) => prev.filter((id) => !removedContentIds.includes(id)))
          }
          if (newContentItems.length > 0) {
            setSelectedContentIds((prev) => [...prev, ...newContentItems.map((item) => item.id)])
          }
          exitReorderMode()
        },
      }
    )
  }, [
    exitReorderMode,
    getNewContentItems,
    getRemovedContentRefIds,
    getSerializedIndexData,
    reOrderHandlerId,
    submitReorder,
  ])

  const gridTemplateColumns = `repeat(${columns}, minmax(0, 1fr))`
  const columnFields = fields.filter((f) => f.layout.position === 'columns')
  const headerFields = fields.filter((f) => f.layout.position === 'header')

  const handleContentSelection = useCallback((contentIds: string[]) => {
    setSelectedContentIds((prev) => [...prev, ...contentIds])
  }, [])

  const handleUnselectContent = useCallback((contentIds: string[]) => {
    setSelectedContentIds((prev) => prev.filter((id) => !contentIds.includes(id)))
  }, [])

  const mergedContentData = useMemo(() => {
    if (!isReorderMode || pendingContentItems.length === 0) {
      return contentData
    }

    const existingIds = new Set(contentData.map((content) => content.id))
    const pendingOnly = pendingContentItems.filter((item) => !existingIds.has(item.id))
    return pendingOnly.length > 0 ? [...contentData, ...pendingOnly] : contentData
  }, [contentData, isReorderMode, pendingContentItems])

  const pendingContentIdSet = useMemo(() => new Set(pendingContentItems.map((item) => item.id)), [pendingContentItems])

  const selectedContent = useMemo(
    () => mergedContentData.filter((c) => selectedContentIds.includes(c.id)),
    [mergedContentData, selectedContentIds]
  )

  const headerActionPayload = useMemo(
    () => ({
      contentRefs: selectedContentIds,
      ids: selectedContentIds,
    }),
    [selectedContentIds]
  )

  const searchInValue = useCallback((value: unknown, query: string): boolean => {
    if (typeof value === 'string') return value.toLowerCase().includes(query.toLowerCase())
    if (typeof value === 'number') return value.toString().toLowerCase().includes(query.toLowerCase())
    if (Array.isArray(value)) return value.some((item) => searchInValue(item, query))
    if (value !== null && typeof value === 'object') {
      return Object.values(value).some((v) => searchInValue(v, query))
    }
    return false
  }, [])

  const filteredContent = useMemo(() => {
    if (!searchQuery) return selectedContent
    return selectedContent.filter((content) => searchInValue(content, searchQuery))
  }, [selectedContent, searchQuery, searchInValue])

  const noContentSelected = (
    <div className="flex w-full flex-col items-center justify-center gap-y-10 pt-36">
      <IconFolderOpenAngledOutline size={100} className="text-muted-foreground/50" />
      <div className="text-lg text-muted-foreground">No content selected</div>
    </div>
  )

  const noResultsFound = (
    <div className="flex w-full flex-col items-center justify-center gap-y-10 pt-36">
      <IconSearchOutline size={100} className="text-muted-foreground/50" />
      <div className="text-lg text-muted-foreground">No results found</div>
    </div>
  )

  if (!isStatementEditorV2UISchema(uiSchema)) {
    return <StatementEditorV2EmptyResponse />
  }

  return (
    <div className="flex h-full flex-col">
      {hasHeader && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          <ComponentActionsContainer>
            {hasHeaderActions ? (
              <div className="flex items-center gap-0">
                <ContentBlockActions actions={headerActions} formData={headerActionPayload} />
              </div>
            ) : null}
            {tileFilters}
          </ComponentActionsContainer>
        </ComponentHeader>
      )}
      <div style={{ height: contentHeight }} className="grid grid-cols-[max-content_1fr] gap-x-5 pt-5">
        <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg bg-offwhite shadow">
          <ScrollArea className="min-h-0 flex-1 p-5">
            {isReorderMode && reorderSelector ? (
              reorderSelector
            ) : (
              <IndexSelectorV2
                indexData={typedIndexData}
                selectedContentIds={selectedContentIds}
                handleContentSelection={handleContentSelection}
                handleUnselectContent={handleUnselectContent}
              />
            )}
          </ScrollArea>
          {reOrderHandlerId ? (
            <div className="shrink-0 border-t bg-offwhite px-3 py-2">
              <IndexPanelActions
                canEditIndex={!!reOrderHandlerId}
                isReorderMode={isReorderMode}
                isSaving={isSavingReorder}
                onEnterReorderMode={enterReorderMode}
                onSave={handleSaveReorder}
                onCancel={cancelReorder}
                onAdd={() => setIsCreateSheetOpen(true)}
              />
            </div>
          ) : null}
        </div>
        {reOrderHandlerId ? (
          <IndexCreateSheet isOpen={isCreateSheetOpen} onOpenChange={setIsCreateSheetOpen} onAdd={handleAddNode} />
        ) : null}
        {reOrderHandlerId && addContentFormContentId ? (
          <IndexContentCreateSheet
            contentFormContentId={addContentFormContentId}
            isOpen={isAddContentSheetOpen}
            onOpenChange={setIsAddContentSheetOpen}
            onAdd={(fields) => {
              const contentId = handleAddContent(fields)
              if (contentId) {
                setSelectedContentIds((prev) => [...prev, contentId])
              }
            }}
          />
        ) : null}
        <ScrollArea className="h-full w-full">
          <div className="relative flex w-full flex-col">
            <div className="sticky top-0 flex w-full items-start justify-between border-b bg-background/50 px-2 py-1 backdrop-blur">
              <div className="text-lg font-bold">Content</div>
              <SearchInput value={searchQuery} onChange={setSearchQuery} />
            </div>
            {!selectedContentIds.length && noContentSelected}
            {selectedContentIds.length > 0 && filteredContent.length === 0 && noResultsFound}
            {selectedContentIds.length > 0 &&
              filteredContent.map((content) => {
                const contentWithPosition = content as StatementEditorV2ContentDataTypeWithPosition
                const position = contentWithPosition.position ?? []
                const serverActionsDisabled = pendingContentIdSet.has(content.id)
                return (
                  <div key={content.id} className="flex w-full flex-col gap-y-5 border-b pb-10">
                    <div className="flex items-center justify-between pr-2 pt-5">
                      <div className="flex flex-1 justify-between gap-x-2">
                        <div className="flex rounded-md bg-secondary/5 px-3 text-foreground shadow-sm">
                          {Array.isArray(position) && position.length > 0
                            ? position.map((pos: string, index: number, self: string[]) => (
                                <div key={pos} className="flex items-center">
                                  <div className="inline-flex h-full max-w-36 items-center text-xs font-light">
                                    <span className="truncate">{pos}</span>
                                  </div>
                                  <div
                                    className={clsx(
                                      'h-full px-3 text-foreground/50',
                                      index === self.length - 1 ? 'hidden' : ''
                                    )}
                                  >
                                    <IconRightChevronOutline className="size-full" />
                                  </div>
                                </div>
                              ))
                            : null}
                        </div>
                        <div className="flex gap-x-2">
                          <ItemHeaderLayoutV2 headerFields={headerFields} content={content} />
                        </div>
                      </div>
                      <ContentBlockActions
                        actions={actions}
                        formData={content}
                        serverActionsDisabled={serverActionsDisabled}
                      />
                    </div>
                    <div style={{ gridTemplateColumns }} className="grid w-full gap-x-5">
                      <ItemColumnsLayoutV2
                        columnFields={columnFields}
                        content={content}
                        serverActionsDisabled={serverActionsDisabled}
                      />
                    </div>
                  </div>
                )
              })}
          </div>
        </ScrollArea>
      </div>
    </div>
  )
}
